import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Result,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  SafetyCertificateOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import KycService from '../services/kyc';
import { KycStatusResult } from '../types';

const { Title, Text } = Typography;

const KycPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [kycInfo, setKycInfo] = useState<KycStatusResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // KYC 状态展示配置（Didit 状态已由后端映射为以下取值）
  const kycStatusConfig: Record<string, { color: string; text: string }> = {
    not_started: { color: 'default', text: t('kyc.statusNotStarted') },
    pending: { color: 'processing', text: t('kyc.statusPending') },
    under_review: { color: 'warning', text: t('kyc.statusUnderReview') },
    requires_action: { color: 'warning', text: t('kyc.statusRequiresAction') },
    approved: { color: 'success', text: t('kyc.statusApproved') },
    rejected: { color: 'error', text: t('kyc.statusRejected') },
  };

  // 获取 KYC 状态
  const fetchStatus = useCallback(
    async (showSpinner: boolean = true) => {
      if (showSpinner) {
        setLoading(true);
      }
      try {
        const data = await KycService.getKycStatus();
        setKycInfo(data);
        setLoadError(null);
      } catch (error: any) {
        setLoadError(error.response?.data?.message || t('kyc.loadStatusFailed'));
      } finally {
        if (showSpinner) {
          setLoading(false);
        }
      }
    },
    [t]
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // 发起认证：调用 startKyc 获取 Didit 托管验证会话地址并跳转
  const handleStartKyc = async () => {
    setStarting(true);
    try {
      const result = await KycService.startKyc();
      // 已通过的情况下后端可能不返回 redirect_url，直接刷新状态展示结果
      if (result.status === 'approved') {
        message.success(t('kyc.alreadyApproved'));
        await fetchStatus(false);
        return;
      }
      const url = result.redirect_url;
      if (!url) {
        message.error(result.message || t('kyc.noRedirectUrl'));
        return;
      }
      // Didit 为 hosted 验证页，跳转过去完成验证；当前标签直接跳转，返回后刷新状态
      window.location.href = url;
    } catch (error: any) {
      message.error(error.response?.data?.message || t('kyc.startFailed'));
    } finally {
      setStarting(false);
    }
  };

  const renderStatusContent = () => {
    if (!kycInfo) {
      return (
        <Result
          status="warning"
          title={t('kyc.noInfoTitle')}
          subTitle={t('kyc.noInfoSubTitle')}
          extra={
            <Button type="primary" icon={<ReloadOutlined />} onClick={() => fetchStatus()}>
              {t('kyc.refreshStatus')}
            </Button>
          }
        />
      );
    }

    const status = kycInfo.kyc_status;

    if (status === 'approved') {
      return (
        <Result
          status="success"
          icon={<CheckCircleOutlined />}
          title={t('kyc.approvedTitle')}
          subTitle={t('kyc.approvedSubTitle')}
          extra={[
            <Button type="primary" key="dashboard" onClick={() => navigate('/dashboard')}>
              {t('kyc.backToDashboard')}
            </Button>,
            <Button key="refresh" icon={<ReloadOutlined />} onClick={() => fetchStatus()}>
              {t('kyc.refreshStatus')}
            </Button>,
          ]}
        />
      );
    }

    if (status === 'rejected') {
      return (
        <Result
          status="error"
          icon={<CloseCircleOutlined />}
          title={t('kyc.rejectedTitle')}
          subTitle={t('kyc.rejectedSubTitle')}
          extra={[
            <Button type="primary" key="retry" loading={starting} onClick={handleStartKyc}>
              {t('kyc.retry')}
            </Button>,
            <Button key="refresh" icon={<ReloadOutlined />} onClick={() => fetchStatus()}>
              {t('kyc.refreshStatus')}
            </Button>,
          ]}
        />
      );
    }

    // requires_action：Didit 要求补充材料，引导继续认证
    if (status === 'requires_action') {
      return (
        <Result
          icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
          title={t('kyc.requiresActionTitle')}
          subTitle={t('kyc.requiresActionSubTitle')}
          extra={[
            <Button type="primary" key="continue" loading={starting} onClick={handleStartKyc}>
              {t('kyc.continue')}
            </Button>,
            <Button key="refresh" icon={<ReloadOutlined />} onClick={() => fetchStatus()}>
              {t('kyc.refreshStatus')}
            </Button>,
          ]}
        />
      );
    }

    // pending / under_review：审核中
    if (status === 'pending' || status === 'under_review') {
      return (
        <Result
          icon={<ClockCircleOutlined style={{ color: '#2F6BFF' }} />}
          title={t('kyc.reviewingTitle')}
          subTitle={t('kyc.reviewingSubTitle')}
          extra={[
            <Button type="primary" key="continue" loading={starting} onClick={handleStartKyc}>
              {t('kyc.continue')}
            </Button>,
            <Button key="refresh" icon={<ReloadOutlined />} onClick={() => fetchStatus()}>
              {t('kyc.refreshStatus')}
            </Button>,
          ]}
        />
      );
    }

    // not_started：测试阶段无需认证即可收款，同时保留开始认证入口
    if (status === 'not_started') {
      return (
        <div>
          <Alert
            type="info"
            showIcon
            message={t('kyc.testPhaseTitle')}
            description={t('kyc.testPhaseDesc')}
            style={{ marginBottom: 24 }}
          />
          <Result
            icon={<SafetyCertificateOutlined style={{ color: '#2F6BFF' }} />}
            title={t('kyc.notStartedTitle')}
            subTitle={t('kyc.notStartedSubTitle')}
            extra={
              <Button type="primary" size="large" loading={starting} onClick={handleStartKyc}>
                {t('kyc.start')}
              </Button>
            }
          />
        </div>
      );
    }

    // 其他未知状态：兜底渲染，避免出现空白区域
    return (
      <Result
        icon={<SafetyCertificateOutlined style={{ color: '#2F6BFF' }} />}
        title={t('kyc.title')}
        subTitle={t('kyc.unknownStatusSubTitle', { status: status || t('kyc.statusUnknown') })}
        extra={[
          <Button type="primary" key="start" size="large" loading={starting} onClick={handleStartKyc}>
            {t('kyc.start')}
          </Button>,
          <Button key="refresh" icon={<ReloadOutlined />} onClick={() => fetchStatus()}>
            {t('kyc.refreshStatus')}
          </Button>,
        ]}
      />
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" tip={t('kyc.loadingStatus')} />
        </div>
      );
    }

    if (loadError && !kycInfo) {
      return (
        <Result
          status="warning"
          title={t('kyc.loadFailedTitle')}
          subTitle={loadError}
          extra={
            <Button type="primary" icon={<ReloadOutlined />} onClick={() => fetchStatus()}>
              {t('kyc.reload')}
            </Button>
          }
        />
      );
    }

    // 非 MoR 模式无需平台 KYC
    if (kycInfo && kycInfo.mode !== 'mor') {
      return (
        <Result
          status="info"
          title={t('kyc.pspModeTitle')}
          subTitle={t('kyc.pspModeSubTitle')}
          extra={
            <Button type="primary" onClick={() => navigate('/onboarding')}>
              {t('kyc.goOnboarding')}
            </Button>
          }
        />
      );
    }

    return (
      <div>
        {renderStatusContent()}
        {kycInfo?.application && (
          <Descriptions
            title={t('kyc.applicationInfo')}
            bordered
            size="small"
            column={2}
            style={{ marginTop: 24 }}
          >
            <Descriptions.Item label={t('kyc.applicationId')} span={2}>
              {kycInfo.application.id}
            </Descriptions.Item>
            <Descriptions.Item label={t('kyc.provider')}>
              {kycInfo.application.provider_name}
            </Descriptions.Item>
            <Descriptions.Item label={t('kyc.applicationStatus')}>
              {kycStatusConfig[kycInfo.application.status]?.text || kycInfo.application.status}
            </Descriptions.Item>
            <Descriptions.Item label={t('kyc.submittedAt')} span={2}>
              {new Date(kycInfo.application.created_at).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </div>
    );
  };

  const statusTag = kycInfo
    ? kycStatusConfig[kycInfo.kyc_status] || { color: 'default', text: kycInfo.kyc_status }
    : null;

  return (
    <div className="kyc-page">
      {/* 页面头部 */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div className="header-content">
          <Title level={4}>
            <SafetyCertificateOutlined style={{ marginRight: 12, color: '#2F6BFF' }} />
            {t('kyc.pageTitle')}
            {statusTag && kycInfo?.mode === 'mor' && (
              <Tag color={statusTag.color} style={{ marginLeft: 12, verticalAlign: 'middle' }}>
                {statusTag.text}
              </Tag>
            )}
          </Title>
          <Text type="secondary">{t('kyc.pageSubtitle')}</Text>
        </div>
      </div>

      <Card>{renderBody()}</Card>
    </div>
  );
};

export default KycPage;
